import {
  Route,
  ServiceAction,
  ServiceBikePartWithDefaultDurations,
  populateRoute,
} from "@repo/common"
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"
import { request } from "../api"

async function fetchAllPartsAdmin(): Promise<
  ServiceBikePartWithDefaultDurations[]
> {
  return await request<ServiceBikePartWithDefaultDurations[]>({
    url: Route.PART_EVALUATION_CONTROLLER__FIND_ALL,
    method: "GET",
  })
}

async function createPartAdmin(formData: FormData): Promise<string> {
  return await request<string>({
    url: Route.PART_EVALUATION_CONTROLLER__CREATE,
    method: "POST",
    data: formData,
    headers: {
      "Content-Type": "multipart/form-data",
    },
  })
}

async function updatePartAdmin(
  partId: string,
  formData: FormData,
): Promise<void> {
  return await request<void>({
    url: populateRoute(Route.PART_EVALUATION_CONTROLLER__UPDATE, partId),
    method: "PATCH",
    data: formData,
    headers: {
      "Content-Type": "multipart/form-data",
    },
  })
}

async function deletePartAdmin(partId: string): Promise<string> {
  return await request<string>({
    url: populateRoute(Route.PART_EVALUATION_CONTROLLER__DELETE, partId),
    method: "DELETE",
  })
}

async function updatePartAvailableActions(
  partId: string,
  availableActions: ServiceAction[],
): Promise<string> {
  return await request<string>({
    url: populateRoute(
      Route.PART_EVALUATION_CONTROLLER__UPDATE_AVAILABLE_ACTIONS,
      partId,
    ),
    method: "PATCH",
    data: { availableActions },
  })
}

async function updatePartDefaultDuration(
  partId: string,
  action: ServiceAction,
  durationMinutes: number,
): Promise<string> {
  return await request<string>({
    url: populateRoute(
      Route.PART_EVALUATION_CONTROLLER__UPDATE_DEFAULT_DURATION,
      partId,
    ),
    method: "PATCH",
    data: { action, durationMinutes },
  })
}

export function useUpdatePartAvailableActions() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      partId,
      availableActions,
    }: {
      partId: string
      availableActions: ServiceAction[]
    }) => updatePartAvailableActions(partId, availableActions),
    onMutate: async ({ partId, availableActions }) => {
      await queryClient.cancelQueries({
        queryKey: partQueries.adminList().queryKey,
      })
      const previous = queryClient.getQueryData(
        partQueries.adminList().queryKey,
      )
      queryClient.setQueryData<ServiceBikePartWithDefaultDurations[]>(
        partQueries.adminList().queryKey,
        (old) =>
          old?.map((part) =>
            part.id === partId ? { ...part, availableActions } : part,
          ),
      )
      return { previous }
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          partQueries.adminList().queryKey,
          context.previous,
        )
      }
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: partQueries.all() }),
  })
}

export function useUpdatePartDefaultDuration() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      partId,
      action,
      durationMinutes,
    }: {
      partId: string
      action: ServiceAction
      durationMinutes: number
    }) => updatePartDefaultDuration(partId, action, durationMinutes),
    onMutate: async ({ partId, action, durationMinutes }) => {
      await queryClient.cancelQueries({
        queryKey: partQueries.adminList().queryKey,
      })
      const previous = queryClient.getQueryData(
        partQueries.adminList().queryKey,
      )
      queryClient.setQueryData<ServiceBikePartWithDefaultDurations[]>(
        partQueries.adminList().queryKey,
        (old) =>
          old?.map((part) =>
            part.id === partId
              ? {
                  ...part,
                  defaultDurations: {
                    ...part.defaultDurations,
                    [action]: durationMinutes,
                  },
                }
              : part,
          ),
      )
      return { previous }
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          partQueries.adminList().queryKey,
          context.previous,
        )
      }
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: partQueries.all() }),
  })
}

export function useCreatePartAdmin() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (formData: FormData) => createPartAdmin(formData),
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: partQueries.all() }),
  })
}

export function useUpdatePartAdmin() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      partId,
      formData,
    }: {
      partId: string
      formData: FormData
    }) => updatePartAdmin(partId, formData),
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: partQueries.all() }),
  })
}

export function useDeletePartAdmin() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (partId: string) => deletePartAdmin(partId),
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: partQueries.all() }),
  })
}

const partQueries = {
  all: () => ["parts"],
  adminList: () =>
    queryOptions({
      queryKey: [...partQueries.all(), "admin-list"],
      queryFn: () => fetchAllPartsAdmin(),
    }),
}

export default partQueries
